import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { Icon } from '@platform/ui';
import type { IQuickFieldsProps } from './IQuickFieldsProps';
import { quickFieldsCount, quickFieldsOf } from './quickFieldFilters';
import { useAssignedBySupport } from './useAssignedBySupport';
import { useQuickCount } from './useQuickCount';
import { useQuickFieldLabel } from './useQuickFieldLabel';
import { useQuickFieldOptions } from './useQuickFieldOptions';

const ANY: Readonly<Record<string, string>> = { area: 'quickFields.allAreas', assignee: 'quickFields.anyone', assignedBy: 'quickFields.anyone', priority: 'quickFields.any', due: 'quickFields.anyTime' };

/**
 * «Швидкі поля» панелі розширених фільтрів (прототип v4 L611-645): вибори 30px «ПОЛЕ: значення ▾» — напрям (дерево з піддеревом), виконавець,
 * призначив (недоступний, доки API не приймає фільтр), пріоритет, термін; вибраний — акцентні рамка, тло й значення; з вибраними —
 * «N з M» (моноширинний) і «Скинути фільтри».
 */
export function QuickFields({ model, values, onChange }: IQuickFieldsProps) {
  const { t } = useTranslation();
  const { ws, rt } = model;
  const options = useQuickFieldOptions(ws, rt);
  const fields = quickFieldsOf(rt);
  const byOk = useAssignedBySupport(ws, rt.key, fields.includes('assignedBy'));
  const active = quickFieldsCount(values) > 0;
  const count = useQuickCount(model, active ? values : {});
  const set = (field: string, value: string) => {
    const next = { ...values };
    if (value) next[field] = value;
    else delete next[field];
    onChange(next);
  };
  const labelOf = useQuickFieldLabel(rt);
  return (
    <div className="lv-adv__section">
      <span className="lv-adv__caption">{t('quickFields.title')}</span>
      {fields.length > 0 && (
        <div className="lv-qf__bar" role="toolbar" aria-label={t('quickFields.title')}>
          {fields.map((f) => {
            const label = labelOf(f);
            const off = f === 'assignedBy' && !byOk;
            return (
              <label key={f} className={cx('lv-qf', values[f] && 'is-on', off && 'is-off')} title={off ? t('quickFields.assignedByOff') : undefined}>
                <span className="lv-qf__label">{label}:</span>
                <select className="lv-qf__select" value={values[f] ?? ''} aria-label={label} disabled={off} onChange={(e) => set(f, e.target.value)}>
                  <option value="">{t(ANY[f] ?? 'quickFields.any')}</option>
                  {options(f).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Icon name="chevron-down" size={14} className="lv-qf__chevron" />
              </label>
            );
          })}
          {active && count.n !== undefined && count.base !== undefined && <span className="lv-qf__count">{t('quickFields.nOf', { n: count.n, base: count.base })}</span>}
          {active && <button type="button" className="lv-qf__clear" onClick={() => onChange({})}>{t('quickFields.clear')}</button>}
        </div>
      )}
    </div>
  );
}
